import LineChart from '../../components/LineChart.jsx';
import { trendSummary } from '../../lib/score-history.js';
import { STATUS_DOT, driftWords } from '../../lib/security-checks.js';
import { Dot } from './parts.jsx';
import { formatScore } from '../../lib/score-format.js';

const finite = (n) => typeof n === 'number' && Number.isFinite(n);

/** The Secure Score % half of the summary ("Secure Score rose from 64% to 71%"), or null without 2 real values. */
export function secureScoreSummary(history) {
  const pts = (history?.points ?? []).filter((p) => finite(p.secureScorePct));
  if (pts.length < 2) return null;
  const a = Math.round(pts[0].secureScorePct);
  const b = Math.round(pts.at(-1).secureScorePct);
  if (a === b) return `Microsoft Secure Score held at ${b}%`;
  return `Microsoft Secure Score ${b > a ? 'rose' : 'fell'} from ${a}% to ${b}%`;
}

/**
 * Score history (Security, mockup 2), one card in two parts. Left: the onGuard score (blue) and the Microsoft Secure
 * Score percentage (sky) over the last 30 days, one point per day, each series labelled directly, with a legend. Right:
 * the changes since the previous collection from the run's drift, from and to in words. `showTrend` false (a viewer who
 * may not read collections, or a 403) leaves only the changes, without an error.
 */
export default function ScoreHistory({ id, showTrend, history, historyStatus, posture }) {
  return (
    <section
      id={id}
      className="card grid min-w-0 scroll-mt-6 grid-cols-1 gap-6 lg:grid-cols-[3fr_2fr] lg:gap-0"
      aria-label="Score history"
      data-testid="security-score-history"
    >
      {showTrend ? (
        <div className="min-w-0 lg:pr-7">
          <Trend history={history} historyStatus={historyStatus} current={posture?.score} />
        </div>
      ) : null}
      <div className={showTrend ? 'min-w-0 border-t border-ipc-border pt-6 lg:border-l lg:border-t-0 lg:pl-7 lg:pt-0' : 'min-w-0 lg:col-span-2'}>
        <DriftList posture={posture} />
      </div>
    </section>
  );
}

function Trend({ history, historyStatus, current }) {
  const covers = history.coversDays >= 30 ? 30 : history.coversDays;
  const has = history.points.length >= 2;
  const title = has ? `Score history, last ${covers} ${covers === 1 ? 'day' : 'days'}` : 'Score history';
  const summary = [trendSummary(history), secureScoreSummary(history)].filter(Boolean).join('. ');
  const withSecure = history.points.filter((p) => finite(p.secureScorePct)).length >= 1;
  const series = [{ name: 'onGuard score', points: history.points.map((p) => ({ day: p.day, value: p.score })), format: (v) => formatScore(Math.round(v)) }];
  if (withSecure) series.push({ name: 'Secure Score %', points: history.points.map((p) => ({ day: p.day, value: p.secureScorePct })), format: (v) => `${Math.round(v)}%` });
  return (
    <>
      <div className="mb-3.5 flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <h2>{title}</h2>
        {has ? (
          <span className="ml-auto flex gap-4 text-[13px] text-ipc-muted" aria-hidden="true" data-legend="">
            <span className="inline-flex items-center gap-1.5"><Dot color="var(--blue)" />onGuard score</span>
            {withSecure ? <span className="inline-flex items-center gap-1.5"><Dot color="var(--sky)" />Secure Score %</span> : null}
          </span>
        ) : null}
      </div>
      {historyStatus === 'loading' && history.points.length === 0 ? (
        <div className="h-48 rounded-lg bg-ipc-track motion-safe:animate-pulse sm:h-56" role="status"><span className="sr-only">Loading the score history</span></div>
      ) : historyStatus === 'error' ? (
        <p className="text-sm text-ipc-muted" role="note">The score history could not be loaded. The current scores are in the cards above.</p>
      ) : has && summary ? (
        <LineChart series={series} summary={summary} todayKey={history.today} testId="security-score-trend" />
      ) : (
        <p className="text-sm text-ipc-muted" data-testid="security-trend-empty">
          The trend appears after your second daily collection.{finite(current) ? ` Today's onGuard score is ${formatScore(current)}.` : ''}
        </p>
      )}
    </>
  );
}

const DRIFT_TONE = (c) => STATUS_DOT[c?.to] ?? STATUS_DOT.skip;

function DriftList({ posture }) {
  const changes = Array.isArray(posture?.drift?.changes) ? posture.drift.changes.filter((c) => c && c.id) : [];
  const delta = posture?.drift?.scoreDelta;
  return (
    <div data-testid="security-drift">
      <div className="mb-3.5"><h2>Changes since the previous collection</h2></div>
      {finite(delta) && delta !== 0 ? (
        <p className="mb-2 text-sm text-ipc-ink">onGuard score {delta > 0 ? 'up' : 'down'} {Math.abs(delta)} {Math.abs(delta) === 1 ? 'point' : 'points'}.</p>
      ) : null}
      {changes.length === 0 ? (
        <p className="text-sm text-ipc-muted">No check changed status since the previous collection.</p>
      ) : (
        <ul className="divide-y divide-ipc-border">
          {changes.map((c) => (
            <li key={c.id} className="flex items-start gap-3 py-3 first:pt-1" data-drift={c.id}>
              <Dot color={DRIFT_TONE(c)} className="mt-1.5 !h-2.5 !w-2.5" />
              <div className="min-w-0">
                <div className="font-semibold text-ipc-ink">{c.title ?? c.id}</div>
                <div className="mt-0.5 text-[13px] text-ipc-muted">{driftWords(c)}</div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
